import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Type for a single test question progress
export interface TestQuestionProgress {
  questionId: string;
  answer: string | null; 
  timeSpent: number; // in seconds
  isBookmarked: boolean;
  visited: boolean;
}

// Type for the progress of one test session
export interface TestProgress {
  lastUpdated: string;
  currentQuestionIndex: number;
  totalTimeSpent: number; // in seconds
  answers: Record<string, TestQuestionProgress>;
}

// Overall state shape
interface TestSessionProgressState {
  // Map of sessionId -> TestProgress
  progress: { [sessionId: string]: TestProgress };
  activeSessionId: string | null;  // Changed from activeTestId
}

const initialState: TestSessionProgressState = {
  progress: {},
  activeSessionId: null  // Changed from activeTestId
};

export const testSessionProgressSlice = createSlice({
  name: 'testSessionProgress',
  initialState,
  reducers: {
    initTestProgress: (state, action: PayloadAction<{ sessionId: string }>) => {
      const { sessionId } = action.payload;  // Remove questionCount since it's unused
      
      // Initialize progress if it doesn't exist
      if (!state.progress[sessionId]) {
        state.progress[sessionId] = {
          lastUpdated: new Date().toISOString(),
          currentQuestionIndex: 0,
          totalTimeSpent: 0,
          answers: {}
        };
      }
      
      state.activeSessionId = sessionId;  // Changed from activeTestId
    },
    
    updateQuestionAnswer: (state, action: PayloadAction<{
      sessionId: string;  // Changed from testId
      questionId: string;
      answer: any;
      timeSpent?: number;
    }>) => {
      const { sessionId, questionId, answer, timeSpent = 0 } = action.payload;
      
      if (!state.progress[sessionId]) {
        state.progress[sessionId] = {
          lastUpdated: new Date().toISOString(),
          currentQuestionIndex: 0,
          totalTimeSpent: 0,
          answers: {}
        };
      }
      
      state.progress[sessionId].answers[questionId] = {
        ...(state.progress[sessionId].answers[questionId] || {
          questionId,
          isBookmarked: false,
          visited: true,
          timeSpent: 0
        }),
        answer,
        timeSpent: (state.progress[sessionId].answers[questionId]?.timeSpent || 0) + timeSpent
      };
      
      // Update last modified time
      state.progress[sessionId].lastUpdated = new Date().toISOString();
      
      // Update total time spent
      if (timeSpent > 0) {
        state.progress[sessionId].totalTimeSpent += timeSpent;
      }
    },
    
    setCurrentQuestion: (state, action: PayloadAction<{ sessionId: string; questionIndex: number }>) => {
      const { sessionId, questionIndex } = action.payload;
      
      if (!state.progress[sessionId]) {
        return;
      }
      
      state.progress[sessionId].currentQuestionIndex = questionIndex;
      state.progress[sessionId].lastUpdated = new Date().toISOString();
    },
    
    toggleBookmark: (state, action: PayloadAction<{ sessionId: string; questionId: string }>) => {
      const { sessionId, questionId } = action.payload;
      
      if (!state.progress[sessionId] || !state.progress[sessionId].answers[questionId]) {
        return;
      }
      
      state.progress[sessionId].answers[questionId].isBookmarked =
        !state.progress[sessionId].answers[questionId].isBookmarked;
      
      state.progress[sessionId].lastUpdated = new Date().toISOString();
    },
    
    markQuestionVisited: (state, action: PayloadAction<{ sessionId: string; questionId: string }>) => {
      const { sessionId, questionId } = action.payload;
      
      if (!state.progress[sessionId]) {
        state.progress[sessionId] = {
          lastUpdated: new Date().toISOString(),
          currentQuestionIndex: 0,
          totalTimeSpent: 0,
          answers: {}
        };
      }
      
      if (!state.progress[sessionId].answers[questionId]) {
        state.progress[sessionId].answers[questionId] = {
          questionId,
          answer: null,
          timeSpent: 0,
          isBookmarked: false,
          visited: true
        };
      } else {
        state.progress[sessionId].answers[questionId].visited = true;
      }
      
      state.progress[sessionId].lastUpdated = new Date().toISOString();
    },
    
    clearTestProgress: (state, action: PayloadAction<{ sessionId: string }>) => {
      const { sessionId } = action.payload;
      
      if (state.activeSessionId === sessionId) {
        state.activeSessionId = null;
      }
      
      delete state.progress[sessionId];
    }
  }
});

export const { 
  initTestProgress, 
  updateQuestionAnswer,  // Changed from saveAnswer
  setCurrentQuestion, 
  toggleBookmark,
  markQuestionVisited,
  clearTestProgress
} = testSessionProgressSlice.actions;

export default testSessionProgressSlice.reducer;
